// The legal confirmations shown before sign-up and checkout, and the small
// policy links under them.
//
//   - LegalLinks: "Terms of Service · Refund Policy · Privacy Policy".
//     docLang="es" opens the Spanish documents (?doc=es), for Mexico and
//     Costa Rica.
//   - SignupConsentLabel: "I am 18 or older and I agree to the Terms of
//     Service and the Privacy Policy." for the sign-up box. Built from plain
//     t() pieces, like the Studio's billing link, so no <Trans> markup lives
//     in the translations.
//   - SignupCountryField: "Country where you live", the countries sign-up is
//     open to (offeredSignupCountries: the US and its territories, plus Mexico
//     and Costa Rica once LEGAL_STATUS is "final") and "Another country".
//   - ResidenceNotice: the Mexican simplified privacy notice or the Costa Rica
//     consent text, always in Spanish (lang="es"); or, until config/legal.js
//     has the contact details those texts need, "not open there yet"
//     (signupCountryNotYet).
//   - LegalCheckbox: a box that is never pre-ticked. The caller owns its state.
//
// Links open in a new tab by default, so a half-filled form is never lost.
// Paths, countries and versions are in config/legal.js; wording is under
// "legal" in translations/{en,es}/common.json.
import React from "react";
import { useTranslation } from "react-i18next";

import {
  LEGAL_STATUS,
  OTHER_COUNTRY,
  legalContact,
  legalPath,
  offeredSignupCountries,
  residenceNoticeReady,
} from "config/legal";
import "./Legal.css";

const NEW_TAB = { target: "_blank", rel: "noopener noreferrer" };

// The Spanish text whatever the site language. Falls back to the page's own
// t where there is no i18next instance (tests).
const spanishT = (i18n, t) => (typeof i18n?.getFixedT === "function" ? i18n.getFixedT("es") : t);

export function LegalLinks({ items = ["terms", "refunds", "privacy"], newTab = true, docLang }) {
  const { t } = useTranslation();
  const tab = newTab ? NEW_TAB : {};
  return (
    <p className="cn-legal-links">
      {items.map((item, i) => (
        <React.Fragment key={item}>
          {i > 0 && " · "}
          <a href={legalPath(item, docLang)} {...tab}>{t(`legal.links.${item}`)}</a>
        </React.Fragment>
      ))}
    </p>
  );
}

export function SignupConsentLabel({ docLang }) {
  const { t } = useTranslation();
  return (
    <>
      {t("legal.signupConfirmBefore")}{" "}
      <a href={legalPath("terms", docLang)} {...NEW_TAB}>{t("legal.links.terms")}</a>{" "}
      {t("legal.andThe")}{" "}
      <a href={legalPath("privacy", docLang)} {...NEW_TAB}>{t("legal.links.privacy")}</a>.
    </>
  );
}

// The country select on the sign-up forms. Names are sorted in the site
// language; "Another country" comes last. While the legal texts are drafts
// Mexico and Costa Rica are not offered at all. LEGAL_STATUS is passed
// explicitly so tests can open the gate by mocking it.
export function SignupCountryField({ id, value, onChange, describedBy, invalid, disabled, last = false }) {
  const { t, i18n } = useTranslation();
  const collator = new Intl.Collator(i18n?.language || undefined);
  const options = offeredSignupCountries(LEGAL_STATUS)
    .map((code) => ({ code, name: t(`legal.signupCountryNames.${code}`) }))
    .sort((a, b) => collator.compare(a.name, b.name));
  return (
    <div className={last ? "cn-field cn-field-last" : "cn-field"}>
      <label className="cn-label" htmlFor={id}>{t("legal.signupCountryLabel")}</label>
      <select
        id={id}
        className="cn-select"
        name="country"
        required
        autoComplete="country-name"
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-describedby={describedBy || undefined}
        aria-invalid={invalid || undefined}
      >
        <option value="">{t("legal.signupCountryChoose")}</option>
        {options.map(({ code, name }) => (
          <option key={code} value={code}>{name}</option>
        ))}
        <option value={OTHER_COUNTRY}>{t("legal.signupCountryNames.other")}</option>
      </select>
    </div>
  );
}

// Sign-up is closed to Mexico and Costa Rica until the texts are final and
// their notice can be shown in full (residenceNoticeReady): what the page says
// instead, in the site language. null when the country can sign up.
export const signupCountryNotYet = (t, country) =>
  residenceNoticeReady(country, process.env, LEGAL_STATUS)
    ? null
    : t("legal.signupCountryNotYet", { country: t(`legal.signupCountryNames.${country}`) });

// What sign-up shows after a Mexico or Costa Rica choice, in Spanish on any
// site language. `id` is what the select points at with aria-describedby.
//
// The texts are the ones src/legal/privacy.es.js quotes in sections 16 and 17
// as what sign-up shows, word for word, with the address and the privacy
// contact from config (legalContact). LegalConsent.test.js compares the two.
// Without those details the notice would miss a required item, so the page
// says sign-up is not open there yet instead.
export function ResidenceNotice({ id, country }) {
  const { t, i18n } = useTranslation();
  if (country !== "MX" && country !== "CR") return null;
  const closed = signupCountryNotYet(t, country);
  if (closed) return <p className="cn-hint" id={id}>{closed}</p>;

  const es = spanishT(i18n, t);
  const contact = legalContact();
  if (country === "MX") {
    return (
      <div id={id} lang="es">
        <p className="cn-hint">
          <strong>{es("legal.mxPrivacyNoticeTitle")}</strong>{" "}
          {es("legal.mxPrivacyNoticeBody", { address: contact.address })}
        </p>
        <p className="cn-hint">
          {es("legal.mxPrivacyNoticeShare", { privacyContact: contact.privacyContact, contactEmail: contact.contactEmail })}
        </p>
        <p className="cn-hint">
          {es("legal.mxPrivacyNoticeMore")}{" "}
          <a className="cn-legal-inline-link" href={legalPath("privacy", "es", "aviso-de-privacidad-integral")} {...NEW_TAB}>
            {es("legal.mxPrivacyNoticeLink")}
          </a>.
        </p>
      </div>
    );
  }
  return (
    <div id={id} lang="es">
      <p className="cn-hint">
        <strong>{es("legal.crConsentTitle")}</strong>{" "}
        {es("legal.crConsentBody", { address: contact.address, contactEmail: contact.contactEmail })}
      </p>
      <p className="cn-hint">{es("legal.crConsentTransfer")}</p>
      <p className="cn-hint">
        {es("legal.crConsentRights", { privacyContact: contact.privacyContact, contactEmail: contact.contactEmail })}{" "}
        <a className="cn-legal-inline-link" href="/contact" {...NEW_TAB}>{es("legal.crConsentContactLink")}</a>.{" "}
        <a className="cn-legal-inline-link" href={legalPath("privacy", "es", "residentes-de-costa-rica")} {...NEW_TAB}>
          {es("legal.crConsentLink")}
        </a>.
      </p>
    </div>
  );
}

export function LegalCheckbox({ id, checked, onChange, disabled, children, describedBy, invalid }) {
  return (
    <label className="cn-legal-check" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={Boolean(checked)}
        onChange={onChange}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
      />
      <span>{children}</span>
    </label>
  );
}
